<template>
  <div class="irp-contact-list" data-testid="irp-contact-list">
    <BittsEmptyState
      v-if="!rows.length"
      class="irp-contact-list__empty"
      :font-awesome-icon="EMPTY_STATE.icon"
      :title="emptyMessage"
      :description="EMPTY_STATE.description"
      data-testid="irp-contact-list-empty"
    />
    <template v-else>
      <template v-for="(row, index) in rows" :key="`contact_${index}`">
        <div class="irp-contact-list__cell" data-testid="irp-contact-name">
          <span class="irp-contact-list__label">
            {{ row.name.label }}
          </span>
          <div class="irp-contact-list__name">
            <FontAwesomeIcon
              :icon="['fak', 'person']"
              class="irp-contact-list__icon"
            />
            <IRPFieldGridText :text="row.name.value" :is-masked="true" />
          </div>
        </div>

        <div class="irp-contact-list__cell" data-testid="irp-contact-role">
          <span class="irp-contact-list__label">
            {{ row.role.label }}
          </span>
          <IRPFieldGridText :text="row.role.value" :is-masked="true" />
        </div>

        <div class="irp-contact-list__cell" data-testid="irp-contact-email">
          <span class="irp-contact-list__label">
            {{ row.email.label }}
          </span>
          <a
            v-if="row.email.value !== EMPTY_VALUE"
            :href="`mailto:${row.email.value}`"
            :aria-label="`Email ${row.name.value}`"
            class="irp-contact-list__email"
          >
            <IRPFieldGridText :text="row.email.value" :is-masked="true" />
          </a>
          <IRPFieldGridText v-else :text="EMPTY_VALUE" />
        </div>

        <div class="irp-contact-list__cell" data-testid="irp-contact-status">
          <span class="irp-contact-list__label">
            {{ row.status.label }}
          </span>
          <IRPFieldGridText :text="row.status.value" />
        </div>

        <div
          v-if="row.remainingFields.length"
          class="irp-contact-list__all-fields"
        >
          <BittsCollapse
            v-model:is-open="openRows[index]"
            :hide-chevron="true"
            class="irp-contact-list__collapse"
            data-testid="irp-contact-all-fields"
          >
            <template #prefix>
              <FontAwesomeIcon
                :icon="['fak', 'chevron-down']"
                class="irp-contact-list__collapse-chevron"
                :class="{ 'rotate-180': openRows[index] }"
              />
            </template>
            <template #title>
              <span class="irp-contact-list__collapse-title">
                {{ collapseLabel(row.remainingFields.length, index) }}
              </span>
            </template>
            <IRPFieldGrid :fields="row.remainingFields" :has-card="false" />
          </BittsCollapse>
        </div>

        <div
          v-if="index < rows.length - 1"
          class="irp-contact-list__separator"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { BittsCollapse, BittsEmptyState } from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { computed, ref } from 'vue';

import { MDM_PROPERTIES, MDM_TYPES } from '@/constants/mdm';
import type {
  RecordContactType,
  RecordFieldType,
} from '@/types/individual_record';

import IRPFieldGrid from './IRPFieldGrid.vue';
import IRPFieldGridText from './IRPFieldGridText.vue';

const {
  contacts,
  isOwnData = false,
  emptyMessage = 'No contacts to show',
} = defineProps<{
  contacts: RecordContactType[];
  isOwnData?: boolean;
  emptyMessage?: string;
}>();

const EMPTY_VALUE = '—';

const EMPTY_STATE: { description: string; icon: BittsIcon } = {
  description: 'Contacts for this account will appear here.',
  icon: ['fak', 'contacts'],
};

const CONTACT_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.CONTACT];

const LISTED_PROPERTIES: string[] = [
  CONTACT_PROPERTIES.NAME,
  CONTACT_PROPERTIES.TITLE,
  CONTACT_PROPERTIES.EMAIL,
];

function cellOf(
  fields: RecordFieldType[],
  mdmProperty: string,
  fallbackLabel: string,
) {
  const field = fields.find((item) => item.mdm_property === mdmProperty);
  const value = field?.value;
  return {
    label: field?.name || fallbackLabel,
    value:
      value === null || value === undefined || value === ''
        ? EMPTY_VALUE
        : String(value),
  };
}

function statusOf(contact: RecordContactType) {
  if (isOwnData) return 'Known';
  // @ts-expect-error the contacts OAS is missing missing_from_crm
  const missingFromCrm: boolean | undefined = contact.missing_from_crm;
  if (missingFromCrm === undefined) return EMPTY_VALUE;
  return missingFromCrm ? 'New' : 'Known';
}

const rows = computed(() =>
  contacts.map((contact) => ({
    name: cellOf(contact.fields, CONTACT_PROPERTIES.NAME, 'Name'),
    role: cellOf(contact.fields, CONTACT_PROPERTIES.TITLE, 'Role'),
    email: cellOf(contact.fields, CONTACT_PROPERTIES.EMAIL, 'Email'),
    status: { label: 'Status', value: statusOf(contact) },
    remainingFields: contact.fields.filter(
      ({ mdm_property: mdmProperty, value }) =>
        (!mdmProperty || !LISTED_PROPERTIES.includes(mdmProperty)) &&
        value !== null &&
        value !== undefined &&
        value !== '',
    ),
  })),
);

const openRows = ref<Record<number, boolean>>({});

function collapseLabel(fieldCount: number, index: number) {
  return openRows.value[index] ? 'Collapse' : `See all fields (${fieldCount})`;
}
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

.irp-contact-list {
  @apply grid items-center gap-x-24 gap-y-16;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.irp-contact-list__empty {
  grid-column: 1 / -1;
}

.irp-contact-list__cell {
  @apply flex min-w-0 flex-col gap-4 text-base text-neutral-text;
}

.irp-contact-list__label {
  @apply truncate text-sm font-semibold text-neutral-text-weak;
}

.irp-contact-list__name {
  @apply flex min-w-0 items-center gap-8;
}

.irp-contact-list__icon {
  @apply shrink-0 text-neutral-accent;
}

.irp-contact-list__all-fields {
  grid-column: 1 / -1;
}

.irp-contact-list__collapse :deep(.bitts-collapse__header) {
  @apply h-auto;
}

.irp-contact-list__collapse :deep(.bitts-collapse__prefix-wrapper) {
  @apply mr-4 h-auto;
}

.irp-contact-list__collapse-chevron {
  @apply text-12 text-neutral-accent transition-transform duration-200;
}

.irp-contact-list__collapse-title {
  @apply text-sm font-semibold text-neutral-text;
}

.irp-contact-list__collapse :deep(.bitts-collapse__content) {
  @apply pl-16 pt-16;
}

.irp-contact-list__separator {
  @apply border-t border-neutral-border;
  grid-column: 1 / -1;
}

.irp-contact-list__email {
  @apply min-w-0 text-secondary-text-link hover:text-secondary-text-link-hover;
}
</style>
